import { z } from 'zod';

import { SenderIdentityKindSchema } from '../conversation/message_envelope';
import { ApprovalDefaultsSchema, PolicyAccessSchema } from '../policy/policy_access';

/**
 * PROVISIONAL users-API contract.
 *
 * Request/response Zod shapes for the two operations the `conversation` service
 * consumes from the (not-yet-built) `customer` API (worktree `customer-api`):
 *   (a) traveler lookup by normalized identity (phone|email)  → attributes + vollUserId
 *   (b) auth resolution (HRD)  email        → { identity_provider, login_hint }
 *
 * ⚠ PROVISIONAL — defined ahead of the real API to unblock Phases 4/5. RECONCILE
 * with the users-API owner once the real service exists (STATE.md open question).
 * HRD (`email→empresa→IdP`) lives ONLY in the users API (D-MCA-01); conversation
 * transports the result, it never computes it.
 *
 * `vollUserId` is opaque `z.string().min(1)` (D2-02 / Phase 1 D-09) — deliberately
 * the users-API traveler id, NOT the 24-hex accounts-api app-user id schema.
 * Requests are `.strict()` (we build them); responses are non-strict so the real
 * API may add fields without breaking the parse.
 */

// ── (a) Traveler lookup ─────────────────────────────────────────────────────────

export const CustomerApiTravelerLookupRequest = z
  .object({
    kind: SenderIdentityKindSchema,
    value: z.string().min(1).max(320),
  })
  .strict();
export type CustomerApiTravelerLookupRequest = z.infer<typeof CustomerApiTravelerLookupRequest>;

export const CustomerApiTravelerLookupResponse = z.discriminatedUnion('found', [
  z.object({
    found: z.literal(true),
    vollUserId: z.string().min(1),
    phone: z.string().min(1),
    email: z.email(),
    company: z.string().min(1), // companyId (numeric business key, as string)
    language: z.string().optional(), // company default conversation language (`business.clients.language`)
    // L1 enrichment (customer-api now returns these; OPTIONAL here so the
    // conversation client — updated in a follow-up — keeps parsing until it
    // maps them). The producer (customer-api) populates them on every found.
    name: z.string().optional(), // traveler display name
    companyName: z.string().optional(), // company display name
    vip: z.boolean().optional(), // presence of a business.usersVip record
    vipLevel: z.string().nullable().optional(), // usersVip.level (e.g. "PRIME") or null
    // Rateio default do viajante (empresa + centro de custo, ids E nomes) —
    // exigido pelo booking aéreo em `travelers[].costCenters`. OPTIONAL: o
    // produtor (customer-api) lê de `business.user_costcenter` e envia quando há
    // um par concreto; ausente (ex.: "todos os centros de custo") → falha fechado.
    approvalDefaults: ApprovalDefaultsSchema.optional(),
    // Flags de acesso por produto (`policy-api` policyGetAccessModal), só os
    // modais de produto. OPTIONAL: fail-soft no produtor — ausente quando a
    // policy-api não responde ou não há perfil.
    policyAccess: PolicyAccessSchema.optional(),
  }),
  z.object({
    found: z.literal(false),
  }),
]);
export type CustomerApiTravelerLookupResponse = z.infer<typeof CustomerApiTravelerLookupResponse>;

// ── (b) Auth resolution (HRD) ─────────────────────────────────────────────────────
// D-MCA-01: the `email→identity_provider` mapping is the users API's responsibility.
// `identity_provider` is a provisional plain string (the IdP catalog is the owner's
// to pin) — intentionally not a fixed enum here.

export const CustomerApiAuthResolveRequest = z.object({ email: z.email() }).strict();
export type CustomerApiAuthResolveRequest = z.infer<typeof CustomerApiAuthResolveRequest>;

export const CustomerApiAuthResolveResponse = z.object({
  identity_provider: z.string().min(1),
  login_hint: z.string(),
});
export type CustomerApiAuthResolveResponse = z.infer<typeof CustomerApiAuthResolveResponse>;
